import React, { ReactNode, useCallback, useMemo, useState } from 'react'
import { FormSection } from '../../../common/components/SectionedForm'
import { api, buildPaginatedUrl } from '../../../common/api'
import { useTranslation } from 'react-i18next'
import { AuthorizationCode, ServerPage } from '../../../app/types'
import { Key } from '@mui/icons-material'

import { Dialog, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material'
import { Sort, useInfiniteScroll } from '../../../common/components/table/useInfiniteScroll'
import { usePrivilege, useUrlHelpers } from '../../../common/util/hooks'
import { FeaturePrivileges } from '../../../common/util/featurePrivileges'
import { ColumnDefinition, InfiniteScrollTable } from '../../../common/components/table/Table'
import TableRowActionsMenu from '../../../common/components/table/TableRowActionsMenu'
import { sorterHeader } from '../../../common/components/table/SorterHeader'
import { AddButton } from '../../../common/components/AddButton'
import CancelButton from '../../../common/form/CancelButton'
import ErrorAlert from '../../../common/components/ErrorAlert'
import DeletionModal from '../../../common/components/DeletionModal'
import useAuthorizationCodeForm from './useAuthorizationCodeForm'

export const useAuthorizationCodeSection = () : FormSection<AuthorizationCode[]> => {
    const { t } = useTranslation()
    const {createUrl} = useUrlHelpers();
    const [entryToDelete, setEntryToDelete] = useState<AuthorizationCode | null>(null)
    const [entryToUpdate, setEntryToUpdate] = useState<AuthorizationCode | null>(null)
    const { hasAccess: hasWriteAccess, onlyWithAccess: onlyWithWriteAccess } = usePrivilege(FeaturePrivileges.enterpriseSettings.write)

    const fetchEntries = useCallback(
        async (pageParam = 0, sortParam: Sort, searchTermParam: string | null): Promise<ServerPage<AuthorizationCode>> => {
            const { data } = await api.get(buildPaginatedUrl(createUrl('authorization-codes'), pageParam, sortParam, searchTermParam))
            return data
        },
        [createUrl]
    )


    const { sort, setSort, rows, error, fetchNextPage, hasNextPage, isFetching, refetch } = useInfiniteScroll<AuthorizationCode>({
        queryKey: `authorization-code`,
        initialSort: { field: 'code', order: 'asc' },
        fetchFunction: fetchEntries,
    })

    const onSubmit = useCallback(
        async (formData: AuthorizationCode) => {
            const url = createUrl(`authorization-codes${entryToUpdate?.uuid ? `/${entryToUpdate.uuid}` : ''}`)
            if (entryToUpdate?.uuid) {
                await api.put(url, {...formData})
            } else {
                await api.post(url, {...formData})
            }

            setEntryToUpdate(null)

            await refetch()
        },
        [refetch, setEntryToUpdate, entryToUpdate, createUrl]
    )

    const { handleSubmit, form: Form, submitButton: SubmitButton, reset, setValue } = useAuthorizationCodeForm({ entry: entryToUpdate, onSubmit })

    const rowActions = useMemo(
        () => [
            {
                key: 'edit',
                text: t('general.edit'),
                onClick: async (record: AuthorizationCode) => {
                    reset()
                    setEntryToUpdate(record)
                    setValue('code', record.code)
                    setValue('description', record.description)
                    setValue('ncos', record.ncos)
                    return false
                },
                dividerBeneath: true,
                needsWriteAccess: true
            },
            {
                key: 'delete',
                text: t('general.delete'),
                onClick: (record: AuthorizationCode) => {
                    setEntryToDelete(record)
                },
                needsWriteAccess: true
            },
        ],
        [t, setValue, reset]
    )

    // @ts-ignore
    const columns: ColumnDefinition<AuthorizationCode>[] = useMemo(
        () => [
            {
                title: t('contact-center-codes.code'),
                key: 'code',
                sorter: true,
                render: (text: string) => <>{text}</>,
            },
            {
                title: t('contact-center-codes.description'),
                key: 'description',
                sorter: true,
                render: (text: string) => <>{text}</>,
            },
            {
                title: t('users.edit.ncos'),
                key: 'ncos',
                sorter: false,
                render: (text: string) => <>{t(`users.ncos-names.${text}`)}</>,
            },
            ...[{
                    title: '',
                    key: 'uuid',
                    sorter: false,
                    render: (text: string, record: AuthorizationCode) => <TableRowActionsMenu record={record} menuItems={rowActions} readOnly={!hasWriteAccess}/>,
            }],
        ],
        [t, rowActions, hasWriteAccess],
    )

    const sorter = useCallback(col => sorterHeader(col, sort, setSort), [sort, setSort])

    const getRowKey = useCallback(entry => entry.uuid, [])

    const getDeleteUrl = useCallback(i => i ? createUrl(`authorization-codes/${i.uuid}`) : null, [createUrl])

    const buttonPanel = useMemo(
        () => (
            <Grid container item justifyContent={'space-between'} flexDirection={'row'} spacing={2} flexWrap={'nowrap'}>
                {onlyWithWriteAccess(<Grid item>
                        <AddButton
                            onClick={() => {
                                reset()
                                setEntryToUpdate({ code: '', description: '', ncos: '', uuid: '' })
                                return null
                            }}
                            label={`${t('contact-center-codes.create-new')}`}
                        />
                    </Grid>)
                }
            </Grid>
        ),
        [t, reset, onlyWithWriteAccess],
    )

    const entryForm = useMemo(
        () => (
            <form className={'ant-form ant-form-vertical'} onSubmit={handleSubmit} noValidate={true}>
                <Dialog disablePortal open={!!entryToUpdate} onClose={() => setEntryToUpdate(null)}>
                    <DialogTitle>{t(entryToUpdate?.uuid ? 'contact-center-codes.update-dialog-title' : 'contact-center-codes.create-new')}</DialogTitle>
                    <DialogContent>
                        <Form />
                    </DialogContent>
                    <DialogActions>
                        <SubmitButton />
                        <CancelButton onClick={() => setEntryToUpdate(null)} />
                    </DialogActions>
                </Dialog>
            </form>
        ),
        [Form, SubmitButton, entryToUpdate, handleSubmit, t]
    )

    const renderDetails = useCallback(
        (data: AuthorizationCode[]): ReactNode => {
            return (
                <Grid container p={1}>
                    <Grid container xs={12} md={10} mb={3} item justifyContent={'flex-start'} flexDirection={'row'} flexWrap={'nowrap'}>
                        <Grid item>{buttonPanel}</Grid>
                    </Grid>
                    <ErrorAlert showAlert={!!error} />
                    <Grid item xs={12} md={10} >
                        <InfiniteScrollTable<AuthorizationCode>
                            hasNextPage={hasNextPage}
                            data={rows}
                            fetchNextPage={fetchNextPage}
                            isFetching={false}
                            getRowKey={getRowKey}
                            sorter={sorter}
                            columns={columns}
                            tableId={'phonebookTable'}
                        />
                    </Grid>

                    <DeletionModal<AuthorizationCode | null>
                        nameToDelete={ac => (`${ac?.code} ${ac?.description? `(${ac?.description})`: ''}`)}
                        itemToDelete={entryToDelete} deleteUrl={getDeleteUrl} onClose={() => setEntryToDelete(null)} onSuccess={refetch} />

                    {entryForm}
                </Grid>
            )
        },
        [ columns, buttonPanel, entryForm, entryToDelete, error, fetchNextPage, getDeleteUrl, getRowKey, hasNextPage, refetch, rows, sorter]
    )

    return {
        title: t( 'settings.authorization-codes'),
        data: rows,
        isFetching: isFetching || false,
        error: error,
        refetch: refetch,
        renderDetails: renderDetails,
        renderForm: null,
        enabled: () => true,
        icon: () => <Key/>
    }
}